import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button } from "@mui/material";

import { useApi, useCustomer } from "../../../contexts/app/provider";
import { DemandWalkEditsContext } from "../../../contexts/demand_walk_edits";
import { LockForecastContext } from "../../../contexts/lock_forecast";
import { PurchasingCalendarEditsContext } from "../../../contexts/purchasing_calendar_edits";
import { SysMessagesContext } from "../../../contexts/sys_messages/context";
import { SysMessageLevel } from "../../../contexts/sys_messages/type";
import { VersionedDataContext } from "../../../contexts/versioned_data/context";
import { useModal } from "../../../contexts/modal/hooks";
import {
  FeatureFlagName,
  useStartMyFullPullMutation,
} from "../../../services/api/__generated__/backend_gateway-types";
import { convertToClientFeatures } from "../../../types/feature_flags";

// A Full Pull enqueues work that only becomes visible as a run up to ~45
// minutes later — none of the run-in-progress signals flip in the meantime, so
// without a local cooldown a second click would double the pull load against
// the source systems. Persisted in sessionStorage so a page reload inside the
// window doesn't resurrect the option.
const COOLDOWN_MS = 15 * 60 * 1000;
const cooldownKey = (customerId: string | null | undefined) =>
  `fullPullCooldownUntil:${customerId ?? "unknown"}`;

/**
 * useFullPull — the Full Pull trigger as a hook, so the Start Run dropdown can
 * offer it as a menu item ("Full Pull + Start Run") instead of a second
 * standalone button.
 *
 * `enabled` follows the customer's FullPull feature flag alone — flag-scoped
 * for everyone, ROOT included, mirroring the server's contextCanFullPull. The
 * flag value from get_my_customer is already resolved against the viewer's
 * role, so one boolean carries both WHO and WHERE; the server re-checks on the
 * mutation regardless.
 *
 * requestFullPull opens a confirm modal (the action commits ~45 minutes of
 * pulling + a run), re-checks every guard at confirm time — the modal can sit
 * open while a run appears from another tab or the schedule — and only then
 * fires the fire-and-forget mutation.
 */
export const useFullPull = () => {
  const { apiService } = useApi();
  const { customerSettings, selectedCustomerId } = useCustomer();
  const { addSystemMessage } = useContext(SysMessagesContext);
  const { openModal, closeModal } = useModal();
  const { engineInProgress, runInProgress, justTriggeredRun, loading } =
    useContext(VersionedDataContext);
  // Same unsaved-work guards as Start Run: this path also ends in a run that
  // becomes the latest run, so it must not fire mid-edit either.
  const { isLockUnlockLoading } = useContext(LockForecastContext);
  const { editMode: demandWalkEditMode } = useContext(DemandWalkEditsContext);
  const { editMode: purchasingCalendarEditMode } = useContext(
    PurchasingCalendarEditsContext
  );
  const editMode = demandWalkEditMode || purchasingCalendarEditMode;

  const features = useMemo(
    () => convertToClientFeatures(customerSettings?.features),
    [customerSettings]
  );
  const enabled = Boolean(features[FeatureFlagName.FullPull]);

  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null);
  // In-flight guard: the cooldown only starts AFTER the mutation succeeds, so
  // without this a slow mutation leaves the menu item enabled and a second
  // confirm double-fires the pull. Ref for correctness inside the async
  // closure; state so the menu item can render disabled.
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  // (Re)load the per-customer cooldown when the selected customer changes.
  useEffect(() => {
    const stored = Number(
      window.sessionStorage.getItem(cooldownKey(selectedCustomerId)) ?? 0
    );
    setCooldownUntil(stored > Date.now() ? stored : null);
  }, [selectedCustomerId]);

  // Arm an expiry timer for WHATEVER cooldown is active — driven by
  // cooldownUntil itself, not the customer, so the cooldown a successful
  // trigger just created also gets a timer.
  useEffect(() => {
    if (cooldownUntil === null) {
      return;
    }
    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) {
      setCooldownUntil(null);
      return;
    }
    const timer = window.setTimeout(() => setCooldownUntil(null), remaining);
    return () => window.clearTimeout(timer);
  }, [cooldownUntil]);

  const [startMyFullPull] = useStartMyFullPullMutation({
    client: apiService.getClient(),
  });

  const runActive =
    engineInProgress || runInProgress !== null || justTriggeredRun !== null;
  const coolingDown = cooldownUntil !== null && cooldownUntil > Date.now();

  const trigger = useCallback(async () => {
    if (submittingRef.current) {
      return;
    }
    // Re-check at the moment of truth rather than trusting the state that
    // opened the confirm modal.
    if (loading || runActive || coolingDown || editMode || isLockUnlockLoading) {
      addSystemMessage({
        level: SysMessageLevel.WARNING,
        message:
          "Full pull not started — a run is in progress, edits are open, or run status is still loading.",
      });
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await startMyFullPull();
      const confirmation = result.data?.startMyFullPull;
      // The resolver returns null — not a GraphQL error — when the server-side
      // gate denies the request, so Apollo resolves cleanly. Null must read as
      // failure.
      if (!confirmation) {
        addSystemMessage({
          level: SysMessageLevel.DANGER,
          message:
            "Full pull was not started — you don't have access for this customer.",
        });
        return;
      }
      const until = Date.now() + COOLDOWN_MS;
      window.sessionStorage.setItem(
        cooldownKey(selectedCustomerId),
        String(until)
      );
      setCooldownUntil(until);
      addSystemMessage({
        level: SysMessageLevel.SUCCESS,
        message: confirmation,
      });
    } catch (err: any) {
      addSystemMessage({
        level: SysMessageLevel.DANGER,
        message: err?.message || "Failed to start full pull",
      });
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }, [
    loading,
    runActive,
    coolingDown,
    editMode,
    isLockUnlockLoading,
    addSystemMessage,
    startMyFullPull,
    selectedCustomerId,
  ]);

  // The modal provider stores the customActions JSX in state at open time,
  // freezing any closure it carries. Route the confirm click through a ref so
  // it always reaches the CURRENT trigger — whose guard then reads confirm-time
  // state — instead of the one captured when the menu item was clicked. The
  // container re-renders on every run-state change, so the ref stays fresh
  // while the modal sits open.
  const triggerRef = useRef(trigger);
  useEffect(() => {
    triggerRef.current = trigger;
  });

  const requestFullPull = useCallback(() => {
    const onConfirm = () => {
      closeModal();
      void triggerRef.current();
    };
    const onCancel = () => {
      closeModal();
    };
    openModal({
      disableStepViewer: true,
      maxWidth: "sm",
      steps: [
        {
          id: "full_pull_confirm",
          title: "Full pull, then start a run?",
          content: (
            <Box>
              This re-pulls ALL sources — not just this morning&apos;s data —
              then runs transforms and the engine, and becomes the latest run.
              It can take up to ~45 minutes. Use this when there are big
              mid-day changes you need reflected now.
            </Box>
          ),
          customActions: (
            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
              <Button variant="outlined" onClick={onCancel}>
                Cancel
              </Button>
              <Button variant="contained" color="primary" onClick={onConfirm}>
                Full Pull + Start Run
              </Button>
            </Box>
          ),
        },
      ],
    });
  }, [openModal, closeModal]);

  return { enabled, coolingDown, submitting, requestFullPull };
};
